<consent-dialog></consent-dialog>

@if (appLoading || navRestoring) {
  <div class="page-booting" [class.nav-restoring-overlay]="navRestoring && !appLoading" no-padding>
    <loading-host class="loading-host" #loadingHost></loading-host>
    <!--THIS MUST MATCH THE LAYOUT AND CSS IN INDEX.HTML SO THE TRANSITION IS NOT NOTICEABLE -->
    <div class="scroll-content">
      <div class="splash">
        <!-- Coach white-labeling (#3707) Tier C: on a warm boot of a branded
             client, show the coach logo (from the localStorage cache) instead
             of the MH logo. First-ever cold boot / signed-out shows MH. -->
        @if (coachBranding.splashLogoUrl) {
          <div class="logo"><img [src]="coachBranding.splashLogoUrl" alt="" /></div>
        } @else {
          <div class="logo"><img src="assets/imgs/logo.svg" /></div>
        }
        <div class="loading-indicator">
          @if (loadingProgressType==='bar') {
            <div class="app-loading-progress-bar" #progressBar>
              <div class="app-loading-progress-bar-fill" #progressBarFill>
                @if (getCurrentProgressBarValue() < 1) {
                  <div class="app-loading-progress-bar-fill-pulse"></div>
                }
              </div>
            </div>
          } @else if (loadingProgressType==='spinner') {
            <cs-spinner></cs-spinner>
          }
        </div>
        @if (loadingProgressType!=='none') {
          <div class="loading-messages">
            @if (syncRetryMessage) {
              <div class="sync-retry-message">{{ syncRetryMessage }}</div>
            } @else if (firstSyncMessage) {
              <div class="sync-retry-message">{{ firstSyncMessage }}</div>
            } @else {
              <cs-text-spinner [textsId]="loadingTextsId"></cs-text-spinner>
            }
            @if (splashStuck) {
              <div class="splash-stuck-message">{{ isSplashLikelyOffline ? LL.app.splashStuckOfflineMessage() : LL.app.splashStuckMessage() }}</div>
            }
            <!-- Retry also shows on the held offline-first-load splash. That screen is deliberately
                 never "stuck" (no auto-reload, no hang report), so it would otherwise have no way
                 out if the device rejoins a network without firing an `online` event. The same holds
                 for the held account-load splash (#6248), where Retry reloads the app. -->
            @if (splashStuck || offlineFirstLoadBlocked || accountLoadBlocked) {
              <button class="splash-retry-button" [disabled]="offlineFirstLoadChecking" (click)="onSplashRetry()">{{ LL.app.retry() }}</button>
            }
          </div>
        }
      </div>
      <div class="version-holder">
        <div class="version-subtle">{{ version }}</div>
      </div>
    </div>
  </div>
}

@if (!appLoading) {
  <cs-split-pane id="split-pane" [ngClass]="{'small-screen': hasClientSmallScreen(), 'has-bottom-nav': navMenu.shouldShowBottomNav()}" [when]="navMenu.shouldShowMenu()"
    (mouseup)="bugReportFab.dragEnd($event)" (mousemove)="bugReportFab.dragMove($event)"
    (touchend)="bugReportFab.dragEnd($event)" (touchmove)="bugReportFab.dragMove($event)" #splitPane>
    <cs-menu id="main-menu" (onOpen)="navMenu.menuOpen()" (onClose)="navMenu.menuClose()">
      <cs-header [backButton]="false" [disableHideOnScroll]="true" [showConnectionStatus]="false">
        <!-- Coach white-labeling (#3707) Tier B: co-brand the menu header with
             the coach's logo + name, keeping a small "powered by MH" mark.
             Falls back to the plain MH lockup when not branded. Each cs-header
             slot keeps a single projectable root (the [header-left] div and the
             cs-header-title) so content projection works through the @if blocks
             (avoids NG8011); the conditionals live inside each slot. -->
        <div header-left class="menu-icon">
          @if (coachBranding.brandingApplied && coachBranding.brandLogoUrl) {
            <img class="menu-brand-logo" [src]="coachBranding.brandLogoUrl" alt="" />
          } @else {
            <cs-icon name="logo"></cs-icon>
          }
        </div>
        <cs-header-title>
          @if (coachBranding.brandingApplied && (coachBranding.brandLogoUrl || coachBranding.brandName)) {
            <div class="menu-brand-lockup">
              <span class="menu-brand-name">{{ coachBranding.brandName || LL.app.title() }}</span>
              <span class="menu-powered-by">{{ LL.coach.branding.poweredByMh() }}</span>
            </div>
          } @else {
            {{ LL.app.title() }}
          }
        </cs-header-title>
      </cs-header>
      <cs-content>
        <div class="menu-list">
          <div class="menu-top-section">
            @for (p of navMenu.pages; track p) {
              @if (navMenu.isVisible(p) && !navMenu.isSettingsPage(p)) {
                <button [id]="p.id" class="menu-item-button" menuClose
                  [ngClass]="{ 'show-notification-dot': navMenu.isNotificationDotVisible(p),  active: navMenu.isPageActive(p) }"
                  (click)="navMenu.openPageAsync(p)" [disabled]="!navMenu.isEnabled(p)">
                  @if (p.icon) {
                    <cs-icon item-start [name]="p.icon" [fill]="navMenu.isPageActive(p)"></cs-icon>
                  }
                  <cs-label>{{ p.id !== "sidebarUserProfile" ? p.title : userName }}</cs-label>
                </button>
              }
            }
            @if (impersonationUserAuth != null) {
              <button class="menu-item-button" menuClose
                (click)="navMenu.openAdminImpersonatedUserAsync()">
                <cs-icon item-start name="eye"></cs-icon>
                {{ impersonationUserAuth.email }}
              </button>
            }
          </div>
          <div class="menu-bottom-section">
            <button class="menu-item-button" menuClose
              [ngClass]="{ active: navMenu.isSettingsPageActive() }"
              (click)="navMenu.openSettingsAsync()">
              <cs-icon item-start name="settings" [fill]="navMenu.isSettingsPageActive()"></cs-icon>
              <cs-label>{{ LL.navigation.settings() }}</cs-label>
            </button>
            <button class="menu-item-button logout" menuClose
              (click)="logoutAsync()">
              <cs-icon item-start name="log-out"></cs-icon>
              <cs-label>
                {{ LL.profile.menu.logout() }}
              </cs-label>
            </button>
          </div>
        </div>
      </cs-content>
    </cs-menu>
    <loading-host class="loading-host" #loadingHost></loading-host>
    <popover-host class="popover-host" #popoverHost></popover-host>
    <toast-host class="toast-host" #toastHost></toast-host>
    <tutorial-host class="tutorial-host" #tutorialHost></tutorial-host>
    <div class="hint-bubble-host"></div>
    <cs-backdrop></cs-backdrop>
    <div class="split-pane-main-wrapper" main>
      @if (coachManagingClient || coachIntakeBannerClientName) {
        <cs-coach-banner
          [clientDisplayName]="coachManagingClient?.clientDisplayName ?? coachIntakeBannerClientName"
          [clientOnline]="coachManagingClient ? coachPresenceState.clientOnline : undefined"
          (backToMyAccount)="coachManagingClient ? stopManagingClient() : handleCoachIntakeBannerBack()">
        </cs-coach-banner>
      }
      @if (showCoachOfflineOverlay) {
        <div class="coach-offline-overlay">
          <cs-icon name="cloud-offline"></cs-icon>
          <span class="offline-title">{{ LL.coach.offlineOverlay.title() }}</span>
          <span class="offline-message">{{ LL.coach.offlineOverlay.message() }}</span>
        </div>
      }
      @if (coachPresenceState.coachIsManaging) {
        <button type="button" class="coach-viewing-icon" [attr.aria-label]="LL.coach.presence.coachViewing()" (click)="handleCoachViewingIconTap()">
          <cs-icon name="eye" [fill]="true"></cs-icon>
        </button>
      }
      <cs-nav #content [ngClass]="{ 'show-notification-dot': navMenu.isNotificationDotVisibleForAnyPage() }"
      name="app"></cs-nav>
      <!-- Bottom navigation shown when side menu is not visible -->
      @if (navMenu.isBelowSplitPaneBreakpoint()) {
        <cs-bottom-nav
          [items]="navMenu.bottomNavItems"
          [canStartWorkout]="navMenu.canStartWorkout"
          [addButtonEnabled]="isFullAppUnlocked()"
          [visible]="navMenu.isOnRootPage()"
          (itemClick)="navMenu.handleBottomNavItemClick($event)"
          (addClick)="navMenu.handleBottomNavAddClick()"
          (startWorkoutClick)="navMenu.handleBottomNavStartWorkoutClick()">
        </cs-bottom-nav>
      }
    </div>
    <!-- Virtual keyboard now created as popover through PopoverController -->
    @if (shouldDisplayDebugFabButton()) {
      <cs-fab id="testingFAB" #fab bottom left icon="code-working"
        style="margin-top: 20px; bottom: calc(70px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))">
        <cs-fab-button icon="skip-forward" [label]="LL.debug.nextDay()" id="nextDay"
          (click)="handleFakeNextDayFabButtonClick()">
        </cs-fab-button>
        <cs-fab-button icon="menu" [label]="LL.debug.more()" id="testMenu" (click)="onFabTestMenuClick()">
        </cs-fab-button>
      </cs-fab>
    }
    @if (bugReportFab.shouldDisplayReportBugButton()) {
      <div class="report-bug-fab-container" #bugFAB>
        <cs-fab class="report-bug-fab" bottom left icon="camera" (click)="bugReportFab.reportBugButtonClicked()">
        </cs-fab>
        <div class="drag-handle" (mousedown)="bugReportFab.dragStart($event)" (touchstart)="bugReportFab.dragStart($event)">
          <cs-icon name="drag-indicator"></cs-icon>
        </div>
      </div>
    }
  </cs-split-pane>
}